import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  listAdminMatchesApi,
  getAdminMatchApi,
  deleteAdminMatchApi,
} from "api/admin";
import {
  C,
  Panel,
  Row,
  SkeletonRows,
  Empty,
  MatchStatusChip,
  InfoRow,
  DetailFrame,
  SearchBar,
  FilterPills,
  Pagination,
  formatDate,
  formatPhone,
  relativeTime,
  dangerBtnSm,
  CsvExportButton,
} from "./admin_ui";

export function AdminMatchesList() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const status = params.get("status") || "";
  const search = params.get("q") || "";
  const page = parseInt(params.get("page") || "1", 10) || 1;

  const [searchInput, setSearchInput] = useState(search);
  const [data, setData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    setSearchInput(search);
  }, [search]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError("");
      const r = await listAdminMatchesApi({
        status: status || undefined,
        search: search || undefined,
        page,
        limit: 20,
      });
      if (cancelled) return;
      if (r?.success) setData({ items: r.items || [], total: r.total || 0 });
      else setError(r?.error || "조회 실패");
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [status, search, page]);

  const setParam = (key, value) => {
    const next = new URLSearchParams(params);
    if (value === undefined || value === null || value === "") next.delete(key);
    else next.set(key, value);
    if (key !== "page") next.set("page", "1");
    setParams(next, { replace: false });
  };

  return (
    <>
      <div style={{ display: "flex", alignItems: "flex-end", gap: 12 }}>
        <div style={{ flex: 1 }}>
          <div
            style={{
              fontSize: 24,
              fontWeight: 800,
              color: C.ink,
              letterSpacing: "-0.02em",
            }}
          >
            수리 요청 관리
          </div>
          <div style={{ marginTop: 4, fontSize: 13, color: C.sub }}>
            전체 {data.total.toLocaleString()}건
          </div>
        </div>
        <CsvExportButton kind="matches" />
      </div>

      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          flexWrap: "wrap",
        }}
      >
        <FilterPills
          value={status}
          onChange={(v) => setParam("status", v)}
          options={[
            { label: "전체", value: "" },
            { label: "매칭중", value: "0" },
            { label: "매칭완료", value: "1" },
            { label: "완료", value: "2" },
          ]}
        />
        <div style={{ flex: 1 }} />
        <SearchBar
          value={searchInput}
          onChange={setSearchInput}
          onSubmit={() => setParam("q", searchInput.trim())}
          placeholder="요청자 / 카테고리 / 주소"
        />
      </div>

      <Panel title="수리 요청 목록" count={data.total}>
        {loading ? (
          <SkeletonRows count={8} />
        ) : error ? (
          <Empty label={error} />
        ) : data.items.length === 0 ? (
          <Empty />
        ) : (
          data.items.map((m) => (
            <Row
              key={m.docId}
              onClick={() => navigate(`/miadmin/matches/${m.docId}`)}
              left={
                <span
                  style={{
                    display: "inline-flex",
                    gap: 8,
                    alignItems: "center",
                  }}
                >
                  <span>{m.selectList || "—"}</span>
                  <MatchStatusChip status={m.status} />
                </span>
              }
              mid={`${m.name || "—"}${m.address ? " · " + String(m.address).slice(0, 20) : ""}`}
              right={relativeTime(m.createDate)}
            />
          ))
        )}
        <Pagination
          page={page}
          limit={20}
          total={data.total}
          onChange={(p) => setParam("page", String(p))}
        />
      </Panel>
    </>
  );
}

export function AdminMatchDetail() {
  const { docId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError("");
      const r = await getAdminMatchApi(docId);
      if (cancelled) return;
      if (r?.success) setData(r.data);
      else setError(r?.error || "조회 실패");
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [docId]);

  const handleDelete = async () => {
    if (!window.confirm("이 수리 요청을 삭제(취소) 처리하시겠습니까?")) return;
    setBusy(true);
    const r = await deleteAdminMatchApi(docId);
    setBusy(false);
    if (r?.success) {
      alert("삭제되었습니다.");
      navigate("/miadmin/matches");
    } else {
      alert(r?.error || "삭제 실패");
    }
  };

  const m = data?.match || {};
  return (
    <DetailFrame
      title="수리 요청 상세"
      backTo="/miadmin/matches"
      onBack={() => navigate(-1)}
      badge={data ? <MatchStatusChip status={m.status} /> : null}
      actions={
        !loading && !error ? (
          <button
            type="button"
            onClick={handleDelete}
            disabled={busy}
            style={dangerBtnSm}
          >
            삭제
          </button>
        ) : null
      }
    >
      {loading ? (
        <SkeletonRows count={6} />
      ) : error ? (
        <Empty label={error} />
      ) : (
        <>
          <InfoRow label="docId" value={m.docId} mono />
          <InfoRow label="카테고리" value={m.selectList} />
          <InfoRow
            label="세부 분류"
            value={
              (m.categoryListName || []).join?.(" / ") ?? m.categoryListName
            }
          />
          <InfoRow
            label="요청자"
            value={
              data?.requester
                ? `${data.requester.name} (${data.requester.userType})`
                : m.name
            }
          />
          <InfoRow
            label="요청자 연락처"
            value={formatPhone(data?.requester?.phone || m.phone)}
          />
          <InfoRow
            label="요청자 주소"
            value={data?.requester?.address || m.address}
          />
          <InfoRow label="기계 정보" value={m.machineInfo || m.modelName} />
          <InfoRow label="등록일" value={formatDate(m.createDate)} />
          <div style={{ height: 12 }} />
          <div
            style={{
              fontSize: 13,
              color: C.sub,
              fontWeight: 700,
              padding: "8px 0",
            }}
          >
            요청 내용
          </div>
          <div
            style={{
              padding: "12px 14px",
              background: C.bg,
              border: `1px solid ${C.line}`,
              borderRadius: 10,
              fontSize: 13,
              color: C.body,
              lineHeight: 1.7,
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
              minHeight: 60,
            }}
          >
            {m.body || m.content || m.etc || "—"}
          </div>

          {data?.expert && (
            <>
              <div style={{ height: 14 }} />
              <div
                style={{
                  fontSize: 13,
                  color: C.sub,
                  fontWeight: 700,
                  padding: "8px 0",
                }}
              >
                매칭된 수리기사
              </div>
              <div
                style={{
                  padding: "14px 16px",
                  background: C.greenBg,
                  border: `1px solid ${C.greenBorder}`,
                  borderRadius: 10,
                  display: "flex",
                  flexDirection: "column",
                  gap: 6,
                  fontSize: 13,
                }}
              >
                <div style={{ fontWeight: 700, color: C.ink }}>
                  {data.expert.name}
                </div>
                <div style={{ color: C.body }}>
                  {formatPhone(data.expert.phone)} ·{" "}
                  {data.expert.address || "—"}
                </div>
              </div>
            </>
          )}
        </>
      )}
    </DetailFrame>
  );
}
